import { Autocomplete, Avatar, Stack, TextField, Typography } from '@mui/material'
import { ChevronDownIcon, XIcon } from 'lucide-react'

import { FIELD_FONT_SIZE, FIELD_HEIGHT, FieldLabel } from '@repo/ui'

import { TagChip } from '../TagChip'

export interface MultiAutocompleteSelectOption {
  value: string
  label: string
  avatar?: string | null
}

export interface MultiAutocompleteSelectProps {
  options: MultiAutocompleteSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  label?: string
  placeholder?: string
  helperText?: string
  error?: boolean
  disabled?: boolean
  loading?: boolean
}

export const MultiAutocompleteSelect = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  loading,
}: MultiAutocompleteSelectProps) => {
  const selectedOptions = options.filter(o => value.includes(o.value))

  return (
    <Stack>
      {/* FieldLabel owns the label→control gap (working-conventions §31) — this
          previously hand-rolled a Typography inside a `spacing={1}` Stack,
          which rendered at 4px (this app's 4px base) instead of the canonical
          8px every other field uses. */}
      <FieldLabel label={label} />
      <Autocomplete
        multiple
        fullWidth
        options={options}
        value={selectedOptions}
        onChange={(_, next) => onChange(next.map(o => o.value))}
        getOptionLabel={o => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        disabled={disabled}
        loading={loading}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              {option.avatar ? (
                <Avatar src={option.avatar} alt={option.label} sx={{ width: 32, height: 32 }} />
              ) : (
                <Avatar sx={{ width: 32, height: 32 }}>{option.label[0]}</Avatar>
              )}
              <Typography variant="body2">{option.label}</Typography>
            </Stack>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <TagChip
              {...getTagProps({ index })}
              key={option.value}
              avatar={option.avatar ? <Avatar src={option.avatar} /> : undefined}
              label={option.label}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            placeholder={selectedOptions.length === 0 ? placeholder : undefined}
            error={error}
            helperText={helperText}
            sx={(theme) => ({
              // `&&` outranks MUI's own `.MuiAutocomplete-root .MuiOutlinedInput-root` padding.
              '&& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                borderRadius: '8px',
                // VRG-705 — 49 empty, like every other field. It measured 61.75: the root sat at
                // body1's 20px, so MUI's `line-height: 1.4375em` resolved to 28.75 (the exact
                // trap FIELD_FONT_SIZE documents), plus 9px of Autocomplete padding. The old
                // 47px min-height was a floor it never reached down to. 16px root font gives a
                // 23px line + 15px input padding = 38, and 5.5px top/bottom makes 49. Chips
                // still grow it, which is right.
                fontSize: FIELD_FONT_SIZE,
                minHeight: FIELD_HEIGHT,
                paddingTop: '5.5px',
                paddingBottom: '5.5px',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
            })}
          />
        )}
        popupIcon={<ChevronDownIcon strokeWidth={1} size={20} />}
        clearIcon={<XIcon strokeWidth={1} size={16} />}
        slotProps={{
          paper: {
            // The house trigger→menu gap. Stated in absolute px rather than `mt: 1`,
            // which rendered 4px here; this is an Autocomplete popper, not a Menu, so
            // it cannot spread `MENU_PAPER_SX`.
            sx: { mt: '8px', borderRadius: 2 },
          },
          popper: {
            sx: { width: 'auto !important' },
          },
        }}
      />
    </Stack>
  )
}
